/**
 * Shared types + helpers for the change_booking_allocation RPC.
 *
 * The RPC returns a structured JSONB envelope; domain-level failures surface
 * as ok:false with a reason + conflicts[] rather than raised exceptions.
 * System-level failures (auth, not found) still throw.
 *
 * See supabase/migrations/20260424000001_change_booking_allocation_structured.sql
 */

import { supabase } from './supabase';

export type AllocationConflictLeg = 'target_on_our_dates' | 'our_item_on_their_dates';
export type AllocationConflictKind = 'booking_direct' | 'booking_buffer' | 'blocking_time';
export type AllocationReason =
  | 'item_inactive'
  | 'incompatible_product_type'
  | 'blocked'
  | 'overlap'
  | 'buffer_overlap'
  | 'concurrent_modification';

export interface AllocationConflict {
  leg: AllocationConflictLeg;
  kind: AllocationConflictKind;
  booking_item_id: string | null;
  booking_id: string | null;
  order_id: string | null;
  customer_name: string | null;
  conflict_start: string;
  conflict_end: string;
}

export interface AllocationRpcResult {
  ok: boolean;
  action: 'reassign' | 'swap';
  executed: boolean;
  reason?: AllocationReason;
  conflicts?: AllocationConflict[];
  message?: string;
}

export interface AllocationRpcParams {
  p_booking_item_id: string;
  p_new_inventory_item_id: string;
  p_swap_with_booking_item_id: string | null;
  p_current_inventory_item_id: string | null;
  p_skip_buffer_check: boolean;
  p_dry_run?: boolean;
}

export async function callChangeAllocation(
  params: AllocationRpcParams
): Promise<AllocationRpcResult> {
  const { data, error } = await (supabase.rpc as any)('change_booking_allocation', params);
  if (error) throw error;
  return data as AllocationRpcResult;
}

/**
 * Build a user-facing message from an ok:false RPC result.
 * Prefers naming the conflicting customer/order when we have one.
 */
export function formatAllocationFailure(
  result: AllocationRpcResult,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const firstBookingConflict = (result.conflicts || []).find(
    c => c.kind === 'booking_direct' || c.kind === 'booking_buffer'
  );

  if (firstBookingConflict?.customer_name && firstBookingConflict?.order_id) {
    if (firstBookingConflict.leg === 'our_item_on_their_dates') {
      return t('changeAllocation.reverseLegConflict', {
        customerName: firstBookingConflict.customer_name,
        orderId: firstBookingConflict.order_id,
      });
    }
    return t('changeAllocation.itemAlreadyBookedBy', {
      customerName: firstBookingConflict.customer_name,
      orderId: firstBookingConflict.order_id,
    });
  }

  switch (result.reason) {
    case 'item_inactive':
      return t('changeAllocation.itemInactive');
    case 'incompatible_product_type':
      return t('changeAllocation.incompatibleProductType');
    case 'blocked':
      return t('changeAllocation.itemBlocked');
    case 'overlap':
      return t('changeAllocation.itemAlreadyBooked');
    case 'buffer_overlap':
      return t('changeAllocation.itemBufferOverlap');
    case 'concurrent_modification':
      return t('changeAllocation.concurrentModification');
    default:
      return t('changeAllocation.failedToChange');
  }
}
